import { useQuery } from '@tanstack/react-query';
import { type GeoResponse, geoAPI } from '../api';
import { currencyForCountry, inferCountryFromLocale } from './localeCountry';

export type InferredLocaleSource = 'locale' | 'ip' | 'fallback';

export interface InferredLocale {
  country: string | null;
  currency: string;
  source: InferredLocaleSource;
  isConfirming: boolean;
}

// Cache the IP result for the session — the buyer's network location does not
// change between pricing/billing views, so refetching adds no signal.
const GEO_STALE_TIME_MS = 60 * 60 * 1000;

/**
 * Locale hint + IP confirm, detect + display only (no FX conversion).
 *
 * Returns an instant guess derived from the device locale, then prefers the
 * IP-based result once `GET /geo` confirms it. On loading or error the instant
 * guess is retained so the UI never blocks or flickers to a default.
 */
export function useInferredLocale(): InferredLocale {
  const localeCountry = inferCountryFromLocale();
  const localeCurrency = currencyForCountry(localeCountry);
  const instantSource: InferredLocaleSource = localeCountry ? 'locale' : 'fallback';

  const query = useQuery<GeoResponse>({
    queryKey: ['geo'],
    queryFn: async () => {
      const { data } = await geoAPI.get();
      return data;
    },
    staleTime: GEO_STALE_TIME_MS,
    retry: false,
  });

  const geo = query.data;

  if (geo && geo.source === 'ip') {
    return {
      country: geo.country ?? localeCountry,
      currency: (geo.currency || localeCurrency).toUpperCase(),
      source: 'ip',
      isConfirming: query.isLoading,
    };
  }

  return {
    country: localeCountry,
    currency: localeCurrency,
    source: instantSource,
    isConfirming: query.isLoading,
  };
}
